import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * THE SECURITY CONTRACT of the mail catcher's HTML tab.
 *
 * Captured mail is untrusted HTML from whatever the developer's app (or a
 * colleague's script) sent. It is rendered in an `<iframe sandbox="">`:
 * the EMPTY sandbox value is the strictest one — no scripts, an opaque
 * origin, no forms, no popups, no top navigation. Stricter than
 * ResponseViewer's `allow-scripts` on purpose: mail never needs scripts.
 *
 * Belt: in the packaged app the parent CSP (`tauri.conf.json`) already
 * blocks remote images. Braces: `about:srcdoc` frames inherit that CSP but
 * `tauri dev` has none, so a meta CSP is injected at the very start of the
 * srcdoc (parsed before any `<img>` can be requested) — `default-src
 * 'none'`, `data:` images and inline styles only. `<base target="_blank">`
 * makes links inert: with popups blocked the click does nothing.
 *
 * Fidelity (D9): the message's own inline styles and `<style>` blocks stay
 * untouched; `cid:` images were resolved to `data:` URIs in Rust; the frame
 * itself is always WHITE (`color-scheme: light`), because real mail is
 * rendered on white and a dark frame would misrepresent the email.
 *
 * Guarded by src/tests/mailHtmlPreview.test.js — red if the sandbox prop
 * is dropped, widened, or the meta injection goes.
 */
export const MAIL_PREVIEW_SANDBOX = '';

export const MAIL_PREVIEW_CSP =
  "default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:";

const META_CSP = `<meta http-equiv="Content-Security-Policy" content="${MAIL_PREVIEW_CSP}">`;
const BASE_BLANK = '<base target="_blank">';
// Kept minimal so the message's own styles win: only the ground colour and
// the colour scheme are pinned.
const FRAME_STYLE =
  '<style>html{background:#fff;color-scheme:light}body{margin:0;padding:12px;color:#111}</style>';
const INJECT = META_CSP + BASE_BLANK + FRAME_STYLE;

/**
 * Prepends the CSP/base/ground styles. The HTML parser hoists leading
 * `<meta>`/`<base>`/`<style>` into `<head>` (creating it if absent), so the
 * policy is enforced before the first byte of message content is parsed. A
 * leading doctype stays first so the frame keeps standards mode.
 */
export function buildPreviewSrcDoc(html) {
  const source = typeof html === 'string' ? html : '';
  const doctype = /^\s*<!doctype[^>]*>/i.exec(source);
  if (doctype) return doctype[0] + INJECT + source.slice(doctype[0].length);
  return INJECT + source;
}

/**
 * Double-buffered frame. A srcdoc swap makes an iframe paint blank before the
 * new document arrives — on a dark app that read as a white "blink" every
 * time the owner arrowed to the next message. So the PREVIOUS frame stays
 * mounted underneath until the new one fires `load`, then the new one fades
 * in over it and the old one is dropped. If `load` never fires (a blocked
 * document, jsdom), a short fallback timer finishes the swap anyway so two
 * frames can never stack for good.
 */
export const PREVIEW_SWAP_FALLBACK_MS = 400;

function Frame({ srcDoc, className, onLoad }) {
  // ONE factory for both buffers so the sandbox contract cannot drift
  // between them (the guard test checks every frame it finds).
  return (
    <iframe
      className={className}
      sandbox={MAIL_PREVIEW_SANDBOX}
      srcDoc={srcDoc}
      title="Email preview"
      referrerPolicy="no-referrer"
      onLoad={onLoad}
    />
  );
}

export default function MailHtmlPreview({ html }) {
  const srcDoc = useMemo(() => buildPreviewSrcDoc(html), [html]);
  const keyRef = useRef(0);
  const [frames, setFrames] = useState(() => ({ current: { key: 0, srcDoc, loaded: false }, previous: null }));

  useEffect(() => {
    setFrames((f) => {
      if (f.current.srcDoc === srcDoc) return f;
      keyRef.current += 1;
      return {
        current: { key: keyRef.current, srcDoc, loaded: false },
        // Only a frame that has actually painted is worth keeping underneath.
        previous: f.current.loaded ? f.current : null,
      };
    });
  }, [srcDoc]);

  const finishSwap = (key) =>
    setFrames((f) => (f.current.key === key ? { current: { ...f.current, loaded: true }, previous: null } : f));

  const { key: currentKey, loaded: currentLoaded } = frames.current;
  useEffect(() => {
    if (currentLoaded) return undefined;
    const timer = setTimeout(() => finishSwap(currentKey), PREVIEW_SWAP_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [currentKey, currentLoaded]);

  return (
    <div className="mailc-preview">
      <div className="mailc-preview-stack" data-testid="mail-preview-stack">
        {frames.previous && (
          <Frame key={frames.previous.key} srcDoc={frames.previous.srcDoc} className="mailc-preview-frame mailc-preview-frame--previous" />
        )}
        <Frame
          key={frames.current.key}
          srcDoc={frames.current.srcDoc}
          className={`mailc-preview-frame${frames.current.loaded ? ' mailc-preview-frame--loaded' : ''}`}
          onLoad={() => finishSwap(frames.current.key)}
        />
      </div>
    </div>
  );
}
